import { z } from 'zod';

/**
 * Role categories with fixed rank ranges (owner decision 2026-10-10 #5). Every school role
 * belongs to one category and its rank must sit inside that category's range. The ranges are
 * disjoint and cover 1–99, so a rank always maps to exactly one category.
 *
 * Rank 100 is the TeachPartner Host Admin ceiling (decision 2026-10-10 #4): reserved, never
 * held or assigned inside a school. The school's top role (Tenant Admin) is 99.
 */
export const ROLE_CATEGORIES = [
  { id: 'users', title: 'Users', minRank: 1, maxRank: 10 },
  { id: 'teachers', title: 'Teachers', minRank: 11, maxRank: 50 },
  { id: 'slt', title: 'SLT', minRank: 51, maxRank: 60 },
  { id: 'local_admin', title: 'Local Admin', minRank: 61, maxRank: 80 },
  { id: 'tenant_admin', title: 'Tenant Admin', minRank: 81, maxRank: 99 },
] as const;

export type RoleCategory = (typeof ROLE_CATEGORIES)[number];
export type RoleCategoryId = RoleCategory['id'];

export const ROLE_CATEGORY_IDS = ROLE_CATEGORIES.map((c) => c.id) as [
  RoleCategoryId,
  ...RoleCategoryId[],
];

export const roleCategoryIdSchema = z.enum(ROLE_CATEGORY_IDS);

/** Lowest rank a school role may have. */
export const MIN_SCHOOL_RANK = 1;
/** Highest rank a school role may have (the Tenant Admin). */
export const MAX_SCHOOL_RANK = 99;
/** TeachPartner Host Admin: a reserved ceiling nobody in a school holds or assigns. */
export const HOST_ADMIN_RANK = 100;

export function roleCategory(id: RoleCategoryId): RoleCategory {
  return ROLE_CATEGORIES.find((c) => c.id === id) ?? ROLE_CATEGORIES[0];
}

/** The category whose range holds this rank, or null for 0, 100 (reserved) or non-integers. */
export function categoryForRank(rank: number): RoleCategoryId | null {
  if (!Number.isInteger(rank)) return null;
  return ROLE_CATEGORIES.find((c) => rank >= c.minRank && rank <= c.maxRank)?.id ?? null;
}

export function isRankInCategory(rank: number, category: RoleCategoryId): boolean {
  return categoryForRank(rank) === category;
}

/** A role's category; servers before 2026-10-10 don't send one, so derive it from the rank. */
export function roleCategoryOf(role: { rank: number; category?: string | null }): RoleCategoryId {
  const parsed = roleCategoryIdSchema.safeParse(role.category);
  if (parsed.success) return parsed.data;
  return categoryForRank(Math.min(role.rank, MAX_SCHOOL_RANK)) ?? 'users';
}
